import type { stacks } from "@dropbox/api-v2-client";
import { Menu } from "@dropbox/dig-components/menu";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ArchiveLine,
  DeleteLine,
  DuplicateLine,
  LinkLine,
  NotificationLine,
  NotificationOffLine,
  PersonLeaveLine,
  SettingsLine,
  UpgradeLine,
} from "@dropbox/dig-icons/assets";
import { PAP_Cancel_DashMoreActions } from "@mirage/analytics/events/types/cancel_dash_more_actions";
import { PAP_Click_DashMoreActions } from "@mirage/analytics/events/types/click_dash_more_actions";
import { createUxaElementId } from "@mirage/analytics/uxa";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { showSnackbar } from "@mirage/shared/snackbar";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import { useCallback, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { ArchiveConfirmationModal } from "../ArchiveConfirmationModal";
import { CloneConfirmationModal } from "../CloningConfirmationModal";
import { featureLine, StackAction } from "../constants";
import { useCopyStackUrlToClipboard } from "../CopyStackUrl";
import {
  useArchiveStack,
  useDeleteStackModal,
  useLeaveStack,
  useToggleStackSubscription,
} from "../hooks";
import { useCloneStack } from "../hooks/useCloneStack";
import { useReportStackPapEvent } from "../hooks/useReportStackPapEvent";
import {
  isStackOwner,
  stackHasWritePermissions,
  stackIsArchived,
} from "../utils/helpers";

const actionSurfaceComponent = "stacks_dropdown";

export const StackNavBarActions = ({
  className,
  stack,
}: {
  className?: string;
  stack: stacks.Stack | null;
}) => {
  const currentAccount = useDropboxAccount();
  const reportStackPapEvent = useReportStackPapEvent();
  const { setShowDeleteConfirmationModal, setStackToDelete } =
    useDeleteStackModal();
  const copyStackUrlToClipboard = useCopyStackUrlToClipboard(
    stack,
    actionSurfaceComponent,
  );
  const leaveStack = useLeaveStack(stack, currentAccount);
  const {
    isArchiveModalOpen,
    isArchiveStacksEnabled,
    setIsArchiveModalOpen,
    toggleStackArchiveStatus,
  } = useArchiveStack(stack);
  const {
    handleCloneStack,
    isCloneStackModalOpen,
    isCloneStacksEnabled,
    isCloning,
    setIsCloneStackModalOpen,
  } = useCloneStack(stack);
  const toggleStackSubscription = useToggleStackSubscription();
  const menuOpenerRef = useRef<HTMLDivElement>(null);

  const isOwner = !stack || isStackOwner(stack);
  const canEdit = !stack || stackHasWritePermissions(stack);
  const isNewStack = !stack?.namespace_id;

  const isDirectMember = useMemo<boolean>(() => {
    if (!currentAccount?.email) {
      return false;
    }
    return (stack?.sharing_data?.members || []).some(
      (member) =>
        member.subject?.[".tag"] === "user" &&
        member.subject.email === currentAccount.email,
    );
  }, [stack?.sharing_data?.members, currentAccount?.email]);

  const handleMenuToggle = useCallback(
    (isOpen: boolean) => {
      if (!stack || !stack?.namespace_id) return;

      reportStackPapEvent(
        isOpen
          ? PAP_Click_DashMoreActions({ actionSurfaceComponent })
          : PAP_Cancel_DashMoreActions({ actionSurfaceComponent }),
      );

      if (!isOpen) {
        // Focus the trigger button when the menu is closed via escape key,
        // so that tab focus order is maintained.
        menuOpenerRef.current?.focus();
      }
    },
    [reportStackPapEvent, stack],
  );

  const handleMenuItemSelect = (menuItem: string) => {
    switch (menuItem) {
      case StackAction.COPY_LINK:
        copyStackUrlToClipboard();
        showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
        break;
      case StackAction.DELETE:
        if (stack) {
          setShowDeleteConfirmationModal(true);
          setStackToDelete(stack);
        }
        break;
      case StackAction.LEAVE:
        leaveStack?.();
        break;
      case StackAction.ARCHIVE:
        if (isArchiveStacksEnabled) setIsArchiveModalOpen(true);
        break;
      case StackAction.CLONE:
        if (isCloneStacksEnabled) setIsCloneStackModalOpen(true);
        break;
      case StackAction.SUBSCRIBE:
        toggleStackSubscription(stack, actionSurfaceComponent);
        break;
    }
  };

  return (
    <>
      <Menu.Wrapper
        onSelection={handleMenuItemSelect}
        onToggle={(event) => handleMenuToggle(event.isOpen)}
      >
        {({ getContentProps, getTriggerProps }) => (
          <>
            <div ref={menuOpenerRef} tabIndex={-1}>
              <IconButtonWithTooltip
                className={className}
                data-testid="StackOptions"
                data-uxa-entity-id={stack?.namespace_id}
                data-uxa-log={createUxaElementId("more_actions_button", {
                  actionSurfaceComponent,
                  featureLine,
                })}
                disabled={!currentAccount}
                tooltipProps={{ title: i18n.t("more") }}
                variant="transparent"
                {...getTriggerProps()}
              >
                <UIIcon src={SettingsLine} />
              </IconButtonWithTooltip>
            </div>
            <Menu.Content {...getContentProps()}>
              <Menu.Segment>
                {!isNewStack && (
                  <Menu.ActionItem
                    data-uxa-entity-id={stack?.namespace_id}
                    data-uxa-log={createUxaElementId(
                      "more_actions_copy_button",
                      {
                        actionSurfaceComponent,
                        featureLine,
                      },
                    )}
                    key={StackAction.COPY_LINK}
                    value={StackAction.COPY_LINK}
                    withLeftAccessory={<UIIcon src={LinkLine} size="medium" />}
                  >
                    {i18n.t("copy_link_to_stack")}
                  </Menu.ActionItem>
                )}
                {!isNewStack && currentAccount && (
                  <Menu.ActionItem
                    data-testid="MenuItemSubscribe"
                    data-uxa-entity-id={stack?.namespace_id}
                    data-uxa-log={createUxaElementId(
                      "more_actions_subscribe_button",
                      { actionSurfaceComponent, featureLine },
                    )}
                    key={StackAction.SUBSCRIBE}
                    value={StackAction.SUBSCRIBE}
                    withLeftAccessory={
                      <UIIcon
                        src={
                          stack.user_data?.is_subscribed
                            ? NotificationOffLine
                            : NotificationLine
                        }
                        size="medium"
                      />
                    }
                  >
                    {stack.user_data?.is_subscribed
                      ? i18n.t("disable_stack_notifications")
                      : i18n.t("enable_stack_notifications")}
                  </Menu.ActionItem>
                )}
              </Menu.Segment>
              {!isNewStack && (
                <Menu.Segment>
                  <Menu.ActionItem
                    data-uxa-entity-id={stack?.namespace_id}
                    data-uxa-log={createUxaElementId(
                      "more_actions_clone_button",
                      { actionSurfaceComponent, featureLine },
                    )}
                    disabled={isCloning}
                    key={StackAction.CLONE}
                    value={StackAction.CLONE}
                    withLeftAccessory={
                      <UIIcon src={DuplicateLine} size="medium" />
                    }
                  >
                    {i18n.t("clone_stack")}
                  </Menu.ActionItem>
                  {canEdit && (
                    <Menu.ActionItem
                      data-uxa-entity-id={stack?.namespace_id}
                      data-uxa-log={createUxaElementId(
                        "more_actions_archive_button",
                        { actionSurfaceComponent, featureLine },
                      )}
                      key={StackAction.ARCHIVE}
                      value={StackAction.ARCHIVE}
                      withLeftAccessory={
                        <UIIcon
                          src={
                            stackIsArchived(stack) ? UpgradeLine : ArchiveLine
                          }
                          size="medium"
                        />
                      }
                    >
                      {i18n.t(
                        stackIsArchived(stack)
                          ? "unarchive_stack"
                          : "archive_stack",
                      )}
                    </Menu.ActionItem>
                  )}
                </Menu.Segment>
              )}
              {leaveStack && !isOwner && !isNewStack && (
                <Menu.Segment>
                  <DigTooltip
                    disabled={isDirectMember}
                    placement="right"
                    title={i18n.t("leave_disallowed")}
                  >
                    <Menu.ActionItem
                      data-uxa-entity-id={stack?.namespace_id}
                      data-uxa-log={createUxaElementId(
                        "more_actions_leave_button",
                        { actionSurfaceComponent, featureLine },
                      )}
                      disabled={!isDirectMember}
                      key={StackAction.LEAVE}
                      value={StackAction.LEAVE}
                      withLeftAccessory={
                        <UIIcon src={PersonLeaveLine} size="medium" />
                      }
                    >
                      {i18n.t("leave")}
                    </Menu.ActionItem>
                  </DigTooltip>
                </Menu.Segment>
              )}
              {isOwner && !isNewStack && (
                <Menu.Segment>
                  <Menu.ActionItem
                    data-testid="menuItemDelete"
                    data-uxa-entity-id={stack?.namespace_id}
                    data-uxa-log={createUxaElementId(
                      "more_actions_delete_button",
                      { actionSurfaceComponent, featureLine },
                    )}
                    key={StackAction.DELETE}
                    value={StackAction.DELETE}
                    withLeftAccessory={
                      <Text color="error" style={{ lineHeight: 0 }}>
                        {" "}
                        <UIIcon src={DeleteLine} size="medium" />{" "}
                      </Text>
                    }
                  >
                    <Text color="error">{i18n.t("delete_stack")}</Text>
                  </Menu.ActionItem>
                </Menu.Segment>
              )}
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
      {isArchiveStacksEnabled && (
        <ArchiveConfirmationModal
          isArchived={stackIsArchived(stack)}
          isOpen={isArchiveModalOpen}
          onCancel={() => setIsArchiveModalOpen(false)}
          onConfirm={async () => {
            setIsArchiveModalOpen(false);
            await toggleStackArchiveStatus();
          }}
        />
      )}
      {isCloneStacksEnabled && (
        <CloneConfirmationModal
          isOpen={isCloneStackModalOpen}
          onCancel={() => setIsCloneStackModalOpen(false)}
          onConfirm={async () => {
            await handleCloneStack(uuidv4());
            setIsCloneStackModalOpen(false);
          }}
        />
      )}
    </>
  );
};
